import { useState, useMemo, useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import type { ColDef, GridSizeChangedEvent } from 'ag-grid-community'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import { useStoreModelPlans } from '@/hooks/useStoreModels'
import { mapApiPlanToFrontend } from '@/api/assortmentPlans'
import { Pagination } from '@/components/common/Pagination'
import {
  ActiveVersionCell,
  TextCell,
  StatusBadgeCell,
} from '@/components/AssortmentPlansGrid/cellRenderers'
import type { AssortmentPlan, StoreModelDetailRead } from '@/types'
import { PlanLinkCell } from './PlanLinkCell'

ModuleRegistry.registerModules([AllCommunityModule])

interface StoreModelPlansTabProps {
  model: StoreModelDetailRead
}

export function StoreModelPlansTab({ model }: StoreModelPlansTabProps) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const { data, isLoading } = useStoreModelPlans({
    modelId: model.id,
    page,
    pageSize,
  })

  const plans = useMemo(
    () => (data?.data ?? []).map(mapApiPlanToFrontend),
    [data]
  )
  const totalPages = data ? Math.ceil(data.pagination.total / pageSize) : 0

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize)
    setPage(1)
  }, [])

  const columnDefs: ColDef<AssortmentPlan>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Plan Name',
        flex: 2,
        minWidth: 180,
        cellRenderer: TextCell,
      },
      {
        field: 'hierarchy',
        headerName: 'Hierarchy',
        flex: 3,
        minWidth: 200,
        cellRenderer: TextCell,
      },
      {
        field: 'versions_count',
        headerName: 'Version',
        flex: 1,
        minWidth: 100,
        maxWidth: 200,
        cellRenderer: ActiveVersionCell,
      },
      {
        field: 'owner',
        headerName: 'Owner',
        flex: 1,
        minWidth: 100,
        cellRenderer: TextCell,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        minWidth: 100,
        maxWidth: 130,
        cellRenderer: StatusBadgeCell,
      },
      {
        colId: 'open',
        headerName: '',
        width: 64,
        minWidth: 64,
        maxWidth: 64,
        sortable: false,
        resizable: false,
        cellRenderer: PlanLinkCell,
      },
    ],
    []
  )

  const onGridSizeChanged = useCallback((params: GridSizeChangedEvent) => {
    setTimeout(() => {
      params.api.sizeColumnsToFit()
    }, 10)
  }, [])

  return (
    <div className="w-full">
      <div className="border border-[#e7e7e7] bg-white overflow-hidden">
        <div className="ag-theme-michaels w-full">
          <AgGridReact<AssortmentPlan>
            rowData={plans}
            columnDefs={columnDefs}
            theme="legacy"
            loading={isLoading}
            domLayout="autoHeight"
            rowHeight={68}
            headerHeight={48}
            autoSizeStrategy={{ type: 'fitGridWidth' }}
            onGridSizeChanged={onGridSizeChanged}
            overlayLoadingTemplate="<span>Loading...</span>"
            overlayNoRowsTemplate="<span>No plans are using this store model yet</span>"
            suppressCellFocus={true}
            suppressRowHoverHighlight={false}
            suppressColumnVirtualisation={true}
            suppressMovableColumns={true}
          />
        </div>

        {totalPages > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>
    </div>
  )
}
